/**
 * MorphAPIClient — the SDK transport.
 *
 * One place owns authentication, the Morph service hosts, default headers,
 * retries, timeouts, and error mapping. Every resource (FastApply, Compact,
 * Reflex, …) holds a reference to this client and delegates HTTP to it via
 * `get`/`post`/`delete`/`request`, exactly like the OpenAI SDK.
 *
 * This module is intentionally free of tool imports and Node built-ins so it
 * stays edge-safe (it is reachable from `@morphllm/morphsdk/edge` through the
 * Compact and model-router resources).
 */
import { fetchWithRetry, withTimeout, MorphError, type RetryConfig } from '../tools/utils/resilience.js';
import { logger } from '../logger.js';
import { SDK_VERSION } from '../version.js';
import { toMorphError } from './error.js';

/** The Morph services the SDK talks to. */
const DEFAULT_BASE_URL = 'https://api.morphllm.com';
const DEFAULT_REPOS_URL = 'https://repos.morphllm.com';
const DEFAULT_BROWSER_URL = 'https://browser.morphllm.com';
const DEFAULT_TIMEOUT = 60_000;

const env = (name: string): string | undefined =>
  typeof process !== 'undefined' ? process.env?.[name] : undefined;

const stripTrailingSlash = (url: string): string => url.replace(/\/+$/, '');

export interface MorphAPIClientOptions {
  /** Morph API key. Resolved against `MORPH_API_KEY` at request time if omitted. */
  apiKey?: string;
  /** Primary API host (default `https://api.morphllm.com`). */
  baseURL?: string;
  /** Code-storage host for codebase search and git (default `https://repos.morphllm.com`). */
  reposURL?: string;
  /** Browser-automation host (default `https://browser.morphllm.com`). */
  browserURL?: string;
  /** Default per-request timeout in ms (default 60s). Resources may override per call. */
  timeout?: number;
  /** Retry policy for transient failures. */
  retryConfig?: RetryConfig;
  /** Enable debug logging. */
  debug?: boolean;
}

/** Per-request options accepted by `request`/`get`/`post`/`delete`. */
export interface RequestOptions {
  /** JSON body; serialized with `JSON.stringify`. */
  body?: unknown;
  /** Query parameters; `undefined`/`null` values are dropped. */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Extra headers, merged over (and able to override) the defaults. */
  headers?: Record<string, string>;
  /** Override the timeout for this call. */
  timeout?: number;
  /** Hit a different host than the default (e.g. `this._client.reposURL`). */
  baseURL?: string;
  /** Return the raw `Response` of a successful (2xx) request instead of parsed JSON (for streaming). */
  stream?: boolean;
  /**
   * Return the raw `Response` without throwing on non-2xx and without parsing.
   * For resources that map errors into their own taxonomy (e.g. GitHub).
   */
  raw?: boolean;
  /** Caller-supplied abort signal. */
  signal?: AbortSignal;
}

export class MorphAPIClient {
  /** Explicit key as provided; resolved against env at request time. */
  apiKey?: string;
  baseURL: string;
  reposURL: string;
  browserURL: string;
  /** Explicit default timeout (ms), if set. The request default is applied lazily so
   * resources can read an undefined value and supply their own fallback. */
  timeout?: number;
  retryConfig?: RetryConfig;
  debug: boolean;

  constructor(options: MorphAPIClientOptions = {}) {
    this.apiKey = options.apiKey;
    this.baseURL = stripTrailingSlash(options.baseURL ?? DEFAULT_BASE_URL);
    this.reposURL = stripTrailingSlash(options.reposURL ?? env('MORPH_SEARCH_URL') ?? DEFAULT_REPOS_URL);
    this.browserURL = stripTrailingSlash(
      options.browserURL ?? (env('MORPH_ENVIRONMENT') === 'DEV' ? 'http://localhost:8000' : DEFAULT_BROWSER_URL),
    );
    this.timeout = options.timeout;
    this.retryConfig = options.retryConfig;
    this.debug = options.debug ?? false;
    if (this.debug) logger.enable();
  }

  /** The key actually used for requests: explicit, else `MORPH_API_KEY`. */
  resolveApiKey(): string | undefined {
    return this.apiKey ?? env('MORPH_API_KEY');
  }

  /** Headers shared with tools that bring their own HTTP client (FastApply/WarpGrep via the `openai` package). */
  defaultHeaders(): Record<string, string> {
    return { 'X-Morph-SDK-Version': SDK_VERSION };
  }

  buildURL(path: string, baseURL?: string): string {
    if (/^https?:\/\//i.test(path)) return path;
    const base = stripTrailingSlash(baseURL ?? this.baseURL);
    return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
  }

  private buildHeaders(apiKey: string, extra?: Record<string, string>): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'X-Morph-SDK-Version': SDK_VERSION,
      Authorization: `Bearer ${apiKey}`,
      ...extra,
    };
  }

  private applyQuery(url: string, query?: RequestOptions['query']): string {
    if (!query) return url;
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null) params.set(key, String(value));
    }
    const qs = params.toString();
    return qs ? `${url}${url.includes('?') ? '&' : '?'}${qs}` : url;
  }

  async request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    const apiKey = this.resolveApiKey();
    if (!apiKey) {
      throw new MorphError(
        'Morph API key not found. Set the MORPH_API_KEY environment variable or pass apiKey in config.',
        'missing_api_key',
        401,
      );
    }

    const url = this.applyQuery(this.buildURL(path, opts.baseURL), opts.query);
    const timeout = opts.timeout ?? this.timeout ?? DEFAULT_TIMEOUT;

    const init: RequestInit = {
      method,
      headers: this.buildHeaders(apiKey, opts.headers),
      ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
      ...(opts.signal ? { signal: opts.signal } : {}),
    };

    logger.debug('MorphAPIClient', 'request', { method, url });

    const response = await withTimeout(
      fetchWithRetry(url, init, this.retryConfig ?? {}),
      timeout,
      `Morph request to ${url} timed out after ${timeout}ms`,
    );

    if (opts.raw) return response as unknown as T;
    if (!response.ok) throw await toMorphError(response);
    if (opts.stream) return response as unknown as T;
    if (response.status === 204) return undefined as T;

    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  get<T>(path: string, opts?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, opts);
  }

  post<T>(path: string, opts?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, opts);
  }

  delete<T>(path: string, opts?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, opts);
  }
}
